import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { api } from "../../utils/api";
import Button from "../../components/atoms/Button";
import Textarea from "../../components/atoms/Textarea";
import ApproverSelect from "../../components/ApproverSelect";
import { APP_LABELS } from "../../utils/format";

/**
 * 지점 변경 요청 폼 (새 요청 > 지점 변경).
 * 가격 변경 요청은 몰어드민/매니저어드민의 상품 화면에서 직접 만들 수 있어 노출하지 않는다.
 */
function BranchChangePage() {
  const navigate = useNavigate();
  const { data: me } = useSWR("/manager/change-request/me");
  const { data: branches } = useSWR(
    "/manager/change-request/branches?type=work"
  );

  const [app, setApp] = useState("manageradmin");
  const [selected, setSelected] = useState(() => new Set());
  const [reason, setReason] = useState("");
  const [reviewerId, setReviewerId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  // 앱 변경 시 선택 초기화 (앱이 바뀌면 기준 셋도 바뀌므로)
  useEffect(() => {
    setSelected(new Set());
  }, [app]);

  const currentInfo = me?.permissions?.[app] || null;
  const currentIds = useMemo(() => {
    if (!currentInfo) return [];
    return (currentInfo.branches || []).map((b) => Number(b.id));
  }, [currentInfo]);

  const isSameAsCurrent = useMemo(() => {
    if (selected.size !== currentIds.length) return false;
    for (const id of currentIds) if (!selected.has(id)) return false;
    return true;
  }, [selected, currentIds]);

  const canSubmit =
    !!me &&
    Array.isArray(branches) &&
    selected.size > 0 &&
    !isSameAsCurrent &&
    !!reviewerId &&
    !submitting;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitErr("");
    try {
      const { data } = await api.post("/manager/change-request", {
        type: "branch_change",
        target_id: me.id,
        app,
        after_data: { branches: Array.from(selected) },
        reason: reason || null,
        reviewer_id: reviewerId,
      });
      if (!data?.success) {
        setSubmitErr(data?.alert || "요청을 보내지 못했습니다.");
        return;
      }
      navigate("/sent", { replace: true });
    } catch (err) {
      setSubmitErr("서버에 연결할 수 없습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const branchList = Array.isArray(branches) ? branches : [];
  const currentNames = currentInfo
    ? currentInfo.is_all
      ? "전체"
      : (currentInfo.branches || []).map((b) => b.name).join(", ") || "없음"
    : "...";

  return (
    <Wrapper>
      <Notice>지점 변경 요청을 보낼 수 있습니다.</Notice>
      <Form onSubmit={submit}>
        <Section>
          <Label>본인 정보</Label>
          <InfoBox>
            <InfoTitle>{me?.name || "..."}</InfoTitle>
            <InfoSub>{me?.position}</InfoSub>
          </InfoBox>
        </Section>

        <Section>
          <Label>적용 대상 앱</Label>
          <RadioRow>
            {Object.entries(APP_LABELS).map(([value, label]) => (
              <RadioLabel key={value} selected={app === value}>
                <input
                  type="radio"
                  name="app"
                  value={value}
                  checked={app === value}
                  onChange={() => setApp(value)}
                />
                {label}
              </RadioLabel>
            ))}
          </RadioRow>
          <CurrentLine>현재 지점: {currentNames}</CurrentLine>
          {currentInfo?.is_all && (
            <Hint>
              전체 권한 상태에서는 변경 요청 대신 사장 직접 할당으로 조정해주세요.
            </Hint>
          )}
        </Section>

        <Section>
          <Label>변경할 지점 (다중 선택)</Label>
          <ChipGrid>
            {branchList.length === 0 && <Hint>지점 목록을 불러오는 중...</Hint>}
            {branchList.map((b) => {
              const id = Number(b.id);
              const isSelected = selected.has(id);
              return (
                <Chip
                  key={id}
                  type="button"
                  selected={isSelected}
                  onClick={() => toggle(id)}
                >
                  <CheckMark selected={isSelected}>
                    {isSelected ? "✓" : ""}
                  </CheckMark>
                  {b.name}
                </Chip>
              );
            })}
          </ChipGrid>
          {selected.size > 0 && isSameAsCurrent && (
            <Hint>현재 지점과 동일합니다. 다른 조합을 선택해주세요.</Hint>
          )}
        </Section>

        <Section>
          <Label>사유 (선택)</Label>
          <Textarea
            placeholder="변경이 필요한 사유"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Section>

        <Section>
          <Label>승인자</Label>
          <ApproverSelect
            type="branch_change"
            value={reviewerId}
            onChange={setReviewerId}
          />
        </Section>

        {submitErr && <ErrorText>{submitErr}</ErrorText>}

        <Button type="submit" block disabled={!canSubmit}>
          {submitting ? "전송 중..." : "지점 변경 요청 보내기"}
        </Button>
      </Form>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Notice = styled.div`
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  color: #003580;
  background: #eef3fb;
  border: 1px solid #d6e0f0;
  border-radius: 12px;
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Label = styled.label`
  font-size: 13px;
  color: #555;
`;
const InfoBox = styled.div`
  padding: 14px;
  border: 1px solid #e6e8eb;
  border-radius: 12px;
  background: #ffffff;
`;
const InfoTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #222;
`;
const InfoSub = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #888;
`;
const RadioRow = styled.div`
  display: flex;
  gap: 8px;
`;
const RadioLabel = styled.label`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  font-size: 14px;
  color: ${({ selected }) => (selected ? "#003580" : "#555")};
  background: ${({ selected }) => (selected ? "#eef3fb" : "#fff")};
  border: 1px solid ${({ selected }) => (selected ? "#003580" : "#e6e8eb")};
  border-radius: 12px;
  cursor: pointer;
  input {
    accent-color: #003580;
  }
`;
const CurrentLine = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #888;
`;
const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid ${({ selected }) => (selected ? "#003580" : "#e6e8eb")};
  border-radius: 16px;
  background: ${({ selected }) => (selected ? "#eef3fb" : "#fff")};
  color: ${({ selected }) => (selected ? "#003580" : "#555")};
  font-size: 13px;
  font-weight: ${({ selected }) => (selected ? 500 : 400)};
  cursor: pointer;
`;
const CheckMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: 1px solid ${({ selected }) => (selected ? "#003580" : "#bbb")};
  border-radius: 3px;
  background: ${({ selected }) => (selected ? "#003580" : "#fff")};
  color: #fff;
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
`;
const Hint = styled.div`
  font-size: 12px;
  color: #888;
`;
const ErrorText = styled.div`
  color: #fb4d44;
  font-size: 13px;
`;

export default BranchChangePage;
